import { resolveEventBookingUrl } from '@wineryxplorer/shared';
import type { EventWithVenue } from './types';

interface EventCallsToActionProps {
  event: EventWithVenue;
  isAtCapacity: boolean;
}

/**
 * The two bottom-of-page calls to action: the members-only club pitch and the
 * link out to the event's booking page (hidden when none resolves). Rendered as
 * siblings so the page's vertical rhythm is unchanged.
 */
export const EventCallsToAction = ({ event, isAtCapacity }: EventCallsToActionProps) => {
  const bookingUrl = resolveEventBookingUrl(event, event.venue);

  return (
    <>
      {/* Members Only CTA */}
      {event.audience === 'members_only' && event.status === 'published' && (
        <div className="border-t border-gray-200 pt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <p className="text-amber-800 font-semibold">This event is for wine club members</p>
            <p className="text-sm text-amber-700 mt-1">
              Join the winery's club or allocation list to attend exclusive events like this one.
            </p>
            {event.contactInfo?.email && (
              <a
                href={`mailto:${event.contactInfo.email}?subject=Interested in joining the wine club`}
                className="inline-block mt-3 px-4 py-2 bg-amber-600 text-white font-medium rounded-md hover:bg-amber-700"
              >
                Contact the Winery
              </a>
            )}
          </div>
        </div>
      )}

      {/* Booking link-out: registration happens on the venue's own site (#330) */}
      {bookingUrl && (
        <div className="border-t border-gray-200 pt-6">
          {isAtCapacity ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-800 font-medium">This event is at capacity</p>
              <p className="text-sm text-red-600 mt-1">Registration is currently closed</p>
            </div>
          ) : (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center px-6 py-3 rounded-md bg-primary-700 text-white text-base font-sans tracking-wide hover:bg-primary-800 transition-colors"
            >
              Book on the venue's site
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          )}
        </div>
      )}
    </>
  );
};
